/**
 * PromptsSection: the system prompts behind the automated agents.
 *
 * The dream judge decides what lands in the review queue. It runs unattended
 * on a nightly schedule, so this section exists to close the loop: edit,
 * dry-run against one real trajectory, then activate. It lived at /prompts
 * until TRA-1185 moved it into Settings.
 *
 * `?trace=` is the handoff from /review's "Fix prompt". It prefills the
 * matching editor's test anchor and opens it on the Test tab.
 */

import { useSearchParams } from 'react-router-dom'

import { PromptEditor } from '@/components/prompts/PromptEditor'
import type { SettingsSectionProps } from '@/components/settings/sections'
import { Skeleton } from '@/components/ui/skeleton'
import { useTeam } from '@/contexts/TeamContext'
import { useAgentPrompts } from '@/hooks/useAgentPrompts'

import { SettingsPageHeader } from './SettingsPageHeader'
import { SettingsBlock } from './SettingsSection'

const ANCHOR_PARAM: Record<string, string> = {
  dream_judge: 'trace',
  turn_monitor: 'turn_trace',
}

export function PromptsSection({ section }: SettingsSectionProps): React.JSX.Element {
  const { isRestricted, impersonatingTeamId } = useTeam()
  const [searchParams] = useSearchParams()
  const promptsQuery = useAgentPrompts()

  return (
    <div>
      <SettingsPageHeader section={section} />

      {promptsQuery.isError && (
        <SettingsBlock>
          <p className="text-sm text-destructive" role="alert">
            Could not load prompts.
          </p>
        </SettingsBlock>
      )}

      {promptsQuery.isLoading && (
        <SettingsBlock>
          <Skeleton className="h-64 w-full" />
        </SettingsBlock>
      )}

      {promptsQuery.data?.map((prompt) => {
        const param = ANCHOR_PARAM[prompt.kind]
        return (
          <PromptEditor
            key={prompt.kind}
            prompt={prompt}
            // Restricted demo teams and impersonating staff both read only;
            // the API refuses their writes.
            readOnly={isRestricted || impersonatingTeamId !== null}
            initialAnchor={
              param === undefined ? undefined : (searchParams.get(param) ?? undefined)
            }
          />
        )
      })}
    </div>
  )
}
